<template>
  <div>
    <FilterCard :applied-filter-count="appliedFilterCount">
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Assigned Date"
            label-for="v-ref-no"
          >
            <div class="position-relative">
              <AdvancedDateFilter
                :date-value="filters['order_status_key_12.created_at']"
                :opens="'right'"
                :clear-icon="false"
                @update="updateDateRange('order_status_key_12.created_at', $event)"
              />
            </div>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Rider"
            label-for="rider"
          >
            <v-select
              v-model="filters['staff.id']"
              :options="riderOptions"
              :reduce="option => option.id"
              label="first_name"
              placeholder="Select Rider"
            >
              <template v-slot:option="option">
                {{ option.reference_id }} | {{ option.first_name }} {{ option.last_name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>
      <!-- Apply and Clear Filters -->
      <b-row class="mt-1">
        <b-col>
          <filter-buttons
            :filter-data="filters"
            :apply-filters="applyFilters"
            :clear-filters="clearFilters"
          />
        </b-col>
      </b-row>
    </FilterCard>
    <div>
      <b-card
        no-body
      >
        <div
          class="m-2"
        >
          <b-row>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
            >
              <div>
                <ReportTableButtons
                  :items="items"
                  :json_fieldz="json_fields"
                  :name="`${downloadReportName} - Page ${page}.xls`"
                  :bulkname="`${downloadReportName}.xls`"
                  :fetch="fetchAssignOrderDeliveryRiderNoPagination"
                  :table-fields="tableFields"
                  :table-data="tableData"
                  :title="
                    `Assign Order Delivery Rider - ${meta.from} to ${meta.to} of ${meta.total} entries`
                  "
                  :report-header="reportHeader"
                  :use-queue-download="true"
                  @refresh="fetchAssignOrderDeliveryRider"
                  @queue-download="downloadQueueExcel"
                />
              </div>
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :items="items"
            :fields="fields"
            :json_fields="json_fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div
                class="sortable-header"
              >
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #cell(assign_date)="data">
              {{ __dateTimeFormatter(data.value) }}
            </template>
            <template #cell(customer_phone)="data">
              <div>{{ data.item.customer_phone }}</div>
              <div>{{ data.item.customer_secondary_phone }}</div>
            </template>
            <template #cell(cod_value)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
            <template #cell(rider)="data">
              <div class="d-flex align-items-center width-100">
                <div>
                  <div class="font-weight-bolder">
                    {{ data.item.rider ? data.item.rider : 'N/A' }}
                  </div>
                  <div class="font-small-2">
                    {{ data.item.rider_employee_id && data.item.rider_employee_id ? data.item.rider_employee_id : '' }}
                  </div>
                </div>
              </div>
            </template>
            <template #cell(remark)="data">
              <div
                v-if="data.value"
                v-b-tooltip.hover.bottom.v-primary="{
                  title: data.value,
                  customClass: 'remark-tooltip',
                  boundary: 'window'
                }"
                style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; cursor: pointer;"
              >
                {{ data.value }}
              </div>
              <span v-else>—</span>
            </template>
          </b-table>
        </b-overlay>
        <div class="mx-2 mb-2">
          <b-row
            cols="12"
          >
            <b-col
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label class="width-75">Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total)? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BRow,
  BTable,
  VBModal,
  BOverlay,
  BPagination,
  VBTooltip,
} from 'bootstrap-vue'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FilterButtons from '@/components/Filter/ReportFilterButtons.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'
import getRiderList from './RiderListDropdown'
import { Tablefields } from './TableFields'

const ReportRepository = RepositoryFactory.get('report')
const BusinessRepository = RepositoryFactory.get('businessSetting')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
  },
  components: {
    vSelect,
    AdvancedDateFilter,
    BCard,
    BTable,
    BRow,
    BCol,
    BFormGroup,
    BOverlay,
    BPagination,
    FilterButtons,
    FilterCard,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      riderOptions: [],
      filters: {
        'order_status_key_12.created_at': `${new Date(new Date().setHours(0, 0, 1, 0)).toISOString()} to ${new Date(new Date().setHours(23, 59, 59, 0)).toISOString()}`,
      },
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50, 100, 150, 200],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      fields: Tablefields,
      tableFields: [
        'Assigned Date',
        'Waybill Number',
        'Merchant',
        'Customer Name',
        'Customer Email',
        'Customer Address',
        'Customer Phone',
        { title: 'COD', styles: { halign: 'right' } },
        'Delivery Attempts',
        'Rider',
        'Rider Employee Id',
        'Latest Remark',
      ],
      json_fields: {
        'Row Number': {
          field: 'row_number',
          callback: value => `${value}`,
        },
        'Assigned Date': {
          field: 'assign_date',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        'Waybill Number': 'waybill_number',
        Merchant: {
          field: 'merchant_name',
          callback: value => `"${value}"`,
        },
        'Customer Name': {
          field: 'customer_name',
          callback: value => `"${value}"`,
        },
        'Customer Email': {
          field: 'customer_email',
          callback: value => `"${value}"`,
        },
        'Customer Address': {
          field: 'customer_address',
          callback: value => `"${value}"`, // Wrap the address in double quotes
        },
        'Customer Phone': 'customer_phone',
        'Second Phone Number': 'customer_secondary_phone',
        COD: 'cod_value',
        'Delivery Attempts': 'delivery_attempts',
        Rider: {
          field: 'rider',
          callback: value => `"${value}"`,
        },
        'Rider Employee Id': {
          callback: value => `"${value.rider_employee_id !== null ? `${value.rider_employee_id}` : 'N/A'}"`,
        },
        'Latest Remark': {
          field: 'remark',
          callback: value => `"${value?.replace(/"/g, '""') || ''}"`,
        },
      },
      currentDate: '',
      todayFilterQuery: '',
      downloadReportName: 'Assign-Order-Delivery-Rider',
      items: [],
    }
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    tableData() {
      return this.items.map(item => [
        item.assign_date
          ? new Date(item.assign_date).toLocaleDateString('en-GB', this.format)
          : '-',
        item.waybill_number,
        item.merchant_name,
        item.customer_name,
        item.customer_email,
        item.customer_address,
        [item.customer_phone, item.customer_secondary_phone],
        item.cod_value.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        item.delivery_attempts,
        item.rider,
        item.rider_employee_id ? item.rider_employee_id : 'N/A',
        item.remark || '',
      ])
    },
    reportHeader() {
      const companyName = this.form?.company_name || 'Company'
      const headerParts = [companyName]

      // Add rider name if selected
      if (this.filters['staff.id']) {
        const selectedRider = this.riderOptions.find(
          rider => rider.id === this.filters['staff.id'],
        )
        if (selectedRider) {
          const riderName = `${selectedRider.reference_id} | ${selectedRider.first_name} ${selectedRider.last_name}`
          headerParts.push(riderName)
        }
      }

      // Add assigned date if selected
      if (this.filters['order_status_key_12.created_at']) {
        const dateRange = this.filters['order_status_key_12.created_at']
        if (typeof dateRange === 'string' && dateRange.includes(' to ')) {
          const [startDate, endDate] = dateRange.split(' to ')
          const formattedStartDate = new Date(startDate).toLocaleDateString(
            'en-GB',
          )
          const formattedEndDate = new Date(endDate).toLocaleDateString(
            'en-GB',
          )
          headerParts.push(
            `Assigned Date: ${formattedStartDate} to ${formattedEndDate}`,
          )
        } else if (dateRange && dateRange.startDate && dateRange.endDate) {
          const formattedStartDate = new Date(
            dateRange.startDate,
          ).toLocaleDateString('en-GB')
          const formattedEndDate = new Date(
            dateRange.endDate,
          ).toLocaleDateString('en-GB')
          headerParts.push(
            `Assigned Date: ${formattedStartDate} to ${formattedEndDate}`,
          )
        }
      }

      return `"${headerParts.join(' / ')}"`
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchAssignOrderDeliveryRider()
      },
    },
    deep: true,
    page() {
      this.fetchAssignOrderDeliveryRider()
    },
    perPage() {
      this.fetchAssignOrderDeliveryRider()
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length
    this.setTodayFilter()
    this.fetchBusinessSettings()
    this.fetchAssignOrderDeliveryRider()
    this.riderList()
    this.updateFilterCount()
  },
  methods: {
    async fetchAssignOrderDeliveryRider() {
      this.dataLoading = true
      this.items = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await ReportRepository.getAssignOrderDeliveryRiderReport(this.page, filterQuery, this.perPage))
        // fiter the default date or selected date for table data and meta data
        this.items = data.data.map((item, index) => ({
          ...item,
          row_number: (this.page - 1) * this.perPage + index + 1,
        }))
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async fetchAssignOrderDeliveryRiderNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await ReportRepository.getAssignOrderDeliveryRiderReportNoPagiantion(filterQuery))
        return data.data.map((item, index) => ({
          ...item,
          row_number: index + 1,
        }))
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async downloadQueueExcel() {
      try {
        // eslint-disable-next-line no-underscore-dangle
        this.__showJsonDownloadAlertForAllOrders()
        const filterQuery = this.buildFilterQuery()
        await ReportRepository.queueAssignedOrdersExcelDownload('delivery_rider', filterQuery)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async fetchBusinessSettings() {
      try {
        this.loading = true
        this.form = localStorage.getItem('businessSettings')
          ? JSON.parse(localStorage.getItem('businessSettings'))
          : (await BusinessRepository.getbusinessSettingsResource()).data.data
        if (!localStorage.getItem('businessSettings')) localStorage.setItem('businessSettings', JSON.stringify(this.form))
      } catch (e) {
        // this.convertAndNotifyError(e)
      }
      this.loading = false
    },
    async riderList() {
      this.riderOptions = await getRiderList()
    },
    handleChangePage(page) {
      this.page = page
      this.fetchAssignOrderDeliveryRider()
    },
    setTodayFilter() {
      const currentDate = this.todayFilter()
      this.updateDateRange('order_status_key_12.created_at', currentDate)
    },
    applyFilters() {
      this.fetchAssignOrderDeliveryRider()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {
        'order_status_key_12.created_at': {
          startDate: null,
          endDate: null,
        },
      }
      this.setTodayFilter()
      this.$nextTick(() => {
        this.fetchAssignOrderDeliveryRider()
      })
      this.updateFilterCount()
    },
  },
}
</script>
<style lang="scss" scoped>
@import '../../../@core/scss/vue/libs/vue-select';

.per-page-selector {
    width: 125px;
}
@media (min-width: 768px) and (max-width: 1023px) {
  .per-page-selector {
    width: 200px; /* Shrinks in tablet view */
  }
}
</style>
